/**
 * Phase 67-05 Wave 4 — typed WebSocket wrapper with auto-reconnect.
 *
 * Used by useFleetWebSocket() to stream fleet-state messages from
 * /ws/v1/fleet. Decoupled from React + TanStack Query so the
 * reconnect / backoff logic is unit-testable in isolation.
 *
 * Lifecycle:
 *   - Constructor opens the WS immediately.
 *   - On close (non-1000) → schedules reconnect with exponential
 *     backoff (1s → 2s → 4s → 8s → 16s, capped at 30s).
 *   - After 3 errors inside a 60s window → fires onFallback(true) and
 *     suspends reconnect attempts until the next external recover() call.
 *   - close() cancels the timer + closes the underlying socket
 *     deterministically.
 *
 * Contract notes:
 *   - Auth: token is appended as a query-string param. Frontend reads
 *     localStorage.protos_token (Phase 66 auth). Bearer header upgrade
 *     would also work but browsers can't set custom headers on the WS
 *     handshake.
 *   - Messages are JSON-parsed before delivery to onMessage. Invalid
 *     JSON is logged + dropped (broadcaster contract is strict JSON).
 */

export type FleetMessage =
  | { type: "snapshot"; machines: unknown[]; stale_count: number; ts: string }
  | {
      type: "machine_update";
      cpu_id: string;
      fields: Record<string, unknown>;
      ts: string;
    }
  | { type: "stale_event"; cpu_id: string; ts: string }
  | { type: "audit"; event: string; cpu_id: string; ts: string };

export type WsClientOptions = {
  url: string;
  token: string;
  onMessage: (msg: FleetMessage) => void;
  onOpen?: () => void;
  onClose?: (code: number) => void;
  onFallback?: (fallbackMode: boolean) => void;
  /** Test seam: swap WebSocket constructor for a stub. */
  WebSocketImpl?: typeof WebSocket;
  /** Backoff window for fallback detection (default 60s). */
  errorWindowMs?: number;
  /** Errors-in-window threshold (default 3). */
  errorThreshold?: number;
};

const BACKOFF_STEPS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

export class WsClient {
  private opts: Required<Omit<WsClientOptions, "onMessage" | "onOpen" | "onClose" | "onFallback">> &
    Pick<WsClientOptions, "onMessage" | "onOpen" | "onClose" | "onFallback">;
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backoffIdx = 0;
  private errorTimestamps: number[] = [];
  private fallbackMode = false;
  private closed = false;

  constructor(opts: WsClientOptions) {
    this.opts = {
      url: opts.url,
      token: opts.token,
      onMessage: opts.onMessage,
      onOpen: opts.onOpen,
      onClose: opts.onClose,
      onFallback: opts.onFallback,
      WebSocketImpl:
        opts.WebSocketImpl ?? (globalThis as { WebSocket: typeof WebSocket }).WebSocket,
      errorWindowMs: opts.errorWindowMs ?? 60_000,
      errorThreshold: opts.errorThreshold ?? 3,
    };
    this.open();
  }

  isFallback(): boolean {
    return this.fallbackMode;
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000);
      } catch {
        // already closed — ignore
      }
      this.ws = null;
    }
  }

  /** Resume WS attempts after a previous fallback. */
  recover(): void {
    this.fallbackMode = false;
    this.opts.onFallback?.(false);
    this.backoffIdx = 0;
    this.errorTimestamps = [];
    if (!this.ws || this.ws.readyState === 3 /* CLOSED */) {
      this.open();
    }
  }

  private open(): void {
    if (this.closed) return;
    const sep = this.opts.url.includes("?") ? "&" : "?";
    const fullUrl = `${this.opts.url}${sep}token=${encodeURIComponent(this.opts.token)}`;
    const ws = new this.opts.WebSocketImpl(fullUrl);
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.backoffIdx = 0;
      this.opts.onOpen?.();
    });
    ws.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse((ev as MessageEvent<string>).data) as FleetMessage;
        this.opts.onMessage(msg);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[ws] invalid JSON message dropped", err);
      }
    });
    ws.addEventListener("error", () => {
      this.recordError();
    });
    ws.addEventListener("close", (ev) => {
      const code = (ev as CloseEvent).code ?? 1006;
      this.opts.onClose?.(code);
      if (code === 1000 || this.closed) {
        return;
      }
      // Non-normal close — schedule reconnect unless in fallback mode.
      if (!this.fallbackMode) {
        this.scheduleReconnect();
      }
    });
  }

  private recordError(): void {
    const now = Date.now();
    this.errorTimestamps = [
      ...this.errorTimestamps.filter((t) => now - t < this.opts.errorWindowMs),
      now,
    ];
    if (
      this.errorTimestamps.length >= this.opts.errorThreshold &&
      !this.fallbackMode
    ) {
      this.fallbackMode = true;
      this.opts.onFallback?.(true);
      // One-shot warn — caller's onFallback handler may also emit one.
      // eslint-disable-next-line no-console
      console.warn(
        `[ws] entering polling fallback mode after ${this.errorTimestamps.length} errors in ${this.opts.errorWindowMs}ms`,
      );
    }
  }

  private scheduleReconnect(): void {
    if (this.closed || this.fallbackMode) return;
    const delay = BACKOFF_STEPS_MS[Math.min(this.backoffIdx, BACKOFF_STEPS_MS.length - 1)];
    this.backoffIdx += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }
}
